import { EventInformation, EventPurchaseCard, isStudentCat, requiresMemberCat } from "./EventDetailSections";
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api } from '../App';
export default function EventDetail() {
  const {
    eventId
  } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    membership_number: '',
  });
  const [memberValid, setMemberValid] = useState(null);
  const [validating, setValidating] = useState(false);
  const [quantities, setQuantities] = useState({});
  // Group booking state
  const [groupDays, setGroupDays] = useState([]);
  const [groupSeason, setGroupSeason] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [familySharedEmail, setFamilySharedEmail] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [reminderPhone, setReminderPhone] = useState('');
  const [reminderSubmitted, setReminderSubmitted] = useState(false);
  useEffect(() => {
    api.get(`/api/events/${eventId}`).then(data => {
      setEvent(data.event);
      const types = data.event?.ticket_types || {};
      const q = {};
      Object.keys(types).forEach(k => {
        q[k] = 0;
      });
      setQuantities(q);
      // Deep-link from the "Buy tickets" popup: /events/:id#tickets scrolls to the purchase card
      if (window.location.hash === '#tickets') {
        setTimeout(() => document.getElementById('tickets')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        }), 250);
      }
    }).catch(() => toast.error('We could not find that event - it may have finished or been removed')).finally(() => setLoading(false));
  }, [eventId]);

  // If the day selection changes so a chosen category is no longer offered (e.g. a single-day
  // category whose day was deselected), move that ticket to an offered category.
  useEffect(() => {
    if (!event) return;
    const cats = (event.ticket_categories || []).filter(c => c.visible !== false);
    const days = event.event_days || [];
    const multi = days.length > 1;
    const seasonAllowed = c => !c.fixed_day && (c.season_available !== undefined ? !!c.season_available : Number(c.season_price || 0) > 0) && Number(c.season_price || 0) > 0;
    const offered = c => !!c && (!multi || (c.fixed_day ? !groupSeason && groupDays.includes(c.fixed_day) : !groupSeason || seasonAllowed(c)));
    const verified = memberValid === true;
    const fallback = cats.find(c => offered(c) && (!(c.requires_member || c.id === 'member') || verified))?.id;
    setTickets(prev => prev.some(t => !offered(cats.find(c => c.id === t.category_id))) ? prev.map(t => offered(cats.find(c => c.id === t.category_id)) || !fallback ? t : {
      ...t,
      category_id: fallback
    }) : prev);
  }, [event, groupSeason, groupDays, memberValid]);
  const handleValidateMember = async () => {
    setValidating(true);
    try {
      const res = await api.post('/api/members/ticket-eligibility', memberForm);
      if (res.valid) {
        setMemberValid(true);
        toast.success('Your GS number is eligible for member tickets. Enter each attendee’s own name and GS number below.');
      } else {
        setMemberValid(false);
        toast.error(res.message || 'Member not found');
      }
    } catch (e) {
      setMemberValid(false);
      toast.error(e.message);
    }
    setValidating(false);
  };
  const handleReminder = async () => {
    if (!reminderPhone.trim()) {
      toast.error('Please enter your phone number so we can send the reminder');
      return;
    }
    try {
      await api.post('/api/reminders', {
        event_id: eventId,
        phone_number: reminderPhone
      });
      setReminderSubmitted(true);
      toast.success('Thank you - we will send you a friendly reminder before the event.');
    } catch (e) {
      toast.error(e.message);
    }
  };
  if (loading) return <div className="max-w-6xl mx-auto px-4 py-20"><div className="h-96 bg-[hsl(var(--muted))] rounded-2xl animate-pulse" /></div>;
  if (!event) return <div className="max-w-6xl mx-auto px-4 py-20 text-center"><p>Event not found</p><Link to="/events"><Button>Back to Events</Button></Link></div>;
  const eventDate = new Date(event.date);
  // An event is only 'past' once the end of its LAST day has gone (a multi-day event stays on sale after day 1).
  const lastEventDay = [...(event.event_days || [])].filter(Boolean).sort().pop();
  const eventEnd = lastEventDay ? new Date(`${lastEventDay}T23:59:59`) : eventDate;
  const isPast = eventEnd < new Date();
  const ticketTypes = event.ticket_types || {};
  const visibleCategories = (event.ticket_categories || []).filter(c => c.visible !== false);
  const hasCategories = visibleCategories.length > 0;
  const eventDays = event.event_days || [];
  const isMultiDay = eventDays.length > 1;
  const remaining = event.capacity - (event.tickets_sold || 0);
  const memberVerified = memberValid === true;
  const catById = id => visibleCategories.find(c => c.id === id);
  const availableCategories = visibleCategories.filter(c => !requiresMemberCat(c) || memberVerified);
  const defaultCategoryId = availableCategories[0]?.id;

  // Per-category options: season pass only where the organisers allow it; some categories
  // (e.g. Aatham Aarti) are valid on ONE fixed day — no day selection, one QR, single price.
  const seasonOk = cat => !!cat && !cat.fixed_day && (cat.season_available !== undefined ? !!cat.season_available : Number(cat.season_price || 0) > 0) && Number(cat.season_price || 0) > 0;
  const isFixedDayCat = cat => !!cat && !!cat.fixed_day;
  const todayUK = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  const seasonLastDay = event.season_last_day || [...eventDays].sort()[Math.min(2, eventDays.length - 1)];
  const anySeasonCat = isMultiDay && visibleCategories.some(seasonOk) && (!seasonLastDay || todayUK <= seasonLastDay);
  // A single-day category (e.g. Aatham Aarti) is only offered when the buyer has picked that exact day
  // (not with a season pass, not with other days).
  const catOffered = cat => {
    if (!isMultiDay) return true;
    if (isFixedDayCat(cat)) return !groupSeason && groupDays.includes(cat.fixed_day);
    if (groupSeason) return seasonOk(cat);
    return true;
  };
  const offeredCategories = visibleCategories.filter(catOffered);
  const effectiveDays = groupSeason ? eventDays : isMultiDay ? groupDays : eventDays;
  const daysChosen = groupSeason || !isMultiDay || groupDays.length > 0;
  const ticketDays = t => {
    const cat = catById(t.category_id);
    if (isFixedDayCat(cat)) return [cat.fixed_day];
    return effectiveDays;
  };
  const allDays = tickets.length ? tickets.flatMap(ticketDays) : effectiveDays;
  const lastDay = allDays.length ? allDays.reduce((a, b) => a > b ? a : b) : null;
  const ticketCost = t => {
    const cat = catById(t.category_id);
    if (!cat) return 0;
    if (isFixedDayCat(cat)) return Number(cat.price || 0);
    if (groupSeason) return seasonOk(cat) ? Number(cat.season_price || 0) : 0;
    return Number(cat.price || 0) * (isMultiDay ? groupDays.length : 1);
  };
  const total = hasCategories ? tickets.reduce((s, t) => s + ticketCost(t), 0) : Object.entries(quantities).reduce((sum, [type, qty]) => sum + (ticketTypes[type]?.price || 0) * qty, 0);
  const totalTickets = hasCategories ? tickets.length : Object.values(quantities).reduce((s, q) => s + q, 0);
  const qrCount = hasCategories ? tickets.reduce((n, t) => n + (ticketDays(t).length || 1), 0) : totalTickets;
  const addTicket = () => {
    if (tickets.length >= 10) {
      toast.error('Bookings are limited to 10 tickets - you are very welcome to place a further booking for additional guests');
      return;
    }
    const preferred = availableCategories.find(catOffered)?.id;
    if (!preferred) {
      toast.error('No ticket types are available for the selected day(s) - please choose another day');
      return;
    }
    setTickets([...tickets, {
      _uiId: crypto.randomUUID(),
      category_id: preferred,
      name: '',
      gender: '',
      membership_number: '',
      student_id: '',
      student_expiry: ''
    }]);
  };
  const updateTicket = (i, patch) => setTickets(prev => prev.map((t, idx) => idx === i ? {
    ...t,
    ...patch
  } : t));
  const toggleGroupDay = day => {
    setGroupSeason(false);
    setGroupDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort());
  };
  const formatDay = d => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  const handleCheckout = async () => {
    if (!customerName.trim() || !customerEmail.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    let payload = {
      event_id: eventId,
      customer_name: customerName,
      customer_email: customerEmail,
      family_shared_email: familySharedEmail,
      is_member: memberVerified,
      membership_number: memberVerified ? memberForm.membership_number : null,
      origin_url: window.location.origin
    };
    if (hasCategories) {
      if (tickets.length === 0) {
        toast.error('Please add at least one ticket');
        return;
      }
      for (const t of tickets) {
        const c = catById(t.category_id);
        if (isMultiDay && !daysChosen) {
          toast.error(`Please select day(s)${anySeasonCat ? ' or a season ticket' : ''} first`);
          return;
        }
        if (isMultiDay && isFixedDayCat(c) && !catOffered(c)) {
          toast.error(`"${c.name}" is only available on ${formatDay(c.fixed_day)} — select that day.`);
          return;
        }
        if (groupSeason && !isFixedDayCat(c) && !seasonOk(c)) {
          toast.error(`"${c?.name}" is a day ticket — it is not available as a season ticket. Untick Season or choose specific days.`);
          return;
        }
        if (!t.name.trim()) {
          toast.error('Please enter a name for every ticket');
          return;
        }
        if (!t.gender) {
          toast.error(`Please choose a gender option for ${t.name}`);
          return;
        }
        const cat = catById(t.category_id);
        if (isStudentCat(cat) && t.student_expiry && lastDay && t.student_expiry < lastDay) {
          toast.error(`${t.name}'s student ID expires before the event. It must be valid until ${formatDay(lastDay)}.`);
          return;
        }
      }
      payload = {
        ...payload,
        items: [],
        days: groupSeason ? [] : effectiveDays,
        season: groupSeason,
        tickets: tickets.map(t => ({
          category_id: t.category_id,
          name: t.name.trim(),
          gender: t.gender,
          membership_number: (t.membership_number || '').trim() || null,
          student_id: t.student_id || null,
          student_expiry: t.student_expiry || null
        }))
      };
    } else {
      const items = Object.entries(quantities).filter(([_, q]) => q > 0).map(([type, quantity]) => ({
        type,
        quantity
      }));
      if (items.length === 0) {
        toast.error('Please add at least one ticket to continue');
        return;
      }
      payload = {
        ...payload,
        items
      };
    }
    setCheckingOut(true);
    try {
      const res = await api.post('/api/checkout/tickets', payload);
      if (res.url) window.location.href = res.url;
    } catch (e) {
      toast.error(e.message, {
        duration: 10000
      });
    }
    setCheckingOut(false);
  };
  return <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(255,248,238,1) 0%, rgba(255,255,255,1) 100%)'
      }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/events" className="inline-flex items-center text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--gs-ink))] mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Events
          </Link>
        </div>
      </section>

      <section className="pb-12 sm:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Event Details */}
            <EventInformation event={event} eventDate={eventDate} eventDays={eventDays} handleReminder={handleReminder} isMultiDay={isMultiDay} isPast={isPast} remaining={remaining} reminderPhone={reminderPhone} reminderSubmitted={reminderSubmitted} setReminderPhone={setReminderPhone} />

            {/* Purchase Card — prominent, main focus */}
            {!isPast && <div className="lg:col-span-3">
                <div className="sticky top-20" id="tickets">
                  <EventPurchaseCard familySharedEmail={familySharedEmail} setFamilySharedEmail={setFamilySharedEmail} addTicket={addTicket} anySeasonCat={anySeasonCat} catById={catById} checkingOut={checkingOut} customerEmail={customerEmail} customerName={customerName} daysChosen={daysChosen} eventDays={eventDays} formatDay={formatDay} groupDays={groupDays} groupSeason={groupSeason} handleCheckout={handleCheckout} handleValidateMember={handleValidateMember} hasCategories={hasCategories} isFixedDayCat={isFixedDayCat} isMember={isMember} isMultiDay={isMultiDay} lastDay={lastDay} memberForm={memberForm} memberValid={memberValid} memberVerified={memberVerified} offeredCategories={offeredCategories} qrCount={qrCount} quantities={quantities} setCustomerEmail={setCustomerEmail} setCustomerName={setCustomerName} setGroupDays={setGroupDays} setGroupSeason={setGroupSeason} setIsMember={setIsMember} setMemberForm={setMemberForm} setMemberValid={setMemberValid} setQuantities={setQuantities} setTickets={setTickets} ticketCost={ticketCost} ticketTypes={ticketTypes} tickets={tickets} toggleGroupDay={toggleGroupDay} total={total} totalTickets={totalTickets} updateTicket={updateTicket} validating={validating} visibleCategories={visibleCategories} />
                </div>
              </div>}
          </div>
        </div>
      </section>
    </div>;
}
